import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { Brain, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Conviction-vs-Outcome chart. For every journal entry that has both
// a conviction (1-10) AND a ticker with a price-anchored
// context_snapshot, plot conviction (X) against the ticker's % return
// since the entry was written (Y). Only renders when count >=
// min_for_render so the chart isn't lying with 3 dots.
//
// The Pearson correlation between conviction and return is shown in
// the header — that's the highest-insight metric. Most users find it
// hovers near 0 or even negative, which is the painful but useful
// truth this chart exists to surface.

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover p-2 shadow-md text-xs space-y-0.5">
      <div className="font-mono font-bold text-foreground">{p.ticker}</div>
      {p.title && <div className="text-muted-foreground italic max-w-[260px] truncate">{p.title}</div>}
      <div>Conviction: <span className="font-mono font-bold">{p.conviction}/10</span></div>
      <div>Return since entry:{' '}
        <span className={`font-mono font-bold ${p.return_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {p.return_pct >= 0 ? '+' : ''}{p.return_pct.toFixed(2)}%
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground">
        ${p.anchor_price} → ${p.current_price}
      </div>
    </div>
  );
}

export default function ConvictionOutcomeChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/journal/conviction-vs-outcome`, { withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ points: [], count: 0, min_for_render: 20 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;
  const { points, count, correlation, min_for_render } = data;

  // Below the threshold: show a "keep journaling" placeholder so the
  // user knows the chart EXISTS and what unlocks it.
  if (count < (min_for_render || 20)) {
    return (
      <div
        className="rounded-xl border-2 border-dashed border-violet-300 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-950/20 p-4"
        data-testid="conviction-chart-locked"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          <h3 className="text-sm font-bold text-violet-700 dark:text-violet-300">Conviction vs Outcome chart</h3>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Unlocks at {min_for_render} entries with both a ticker AND a conviction set.
          You have <span className="font-mono font-bold text-foreground">{count}</span>.
          Once it draws, it plots how high-conviction trades actually performed — most investors find their conviction is barely correlated with returns. Useful, painful truth.
        </p>
      </div>
    );
  }

  // Quick label for the correlation. Tone is intentionally sober —
  // this is data, not a victory lap.
  const corrCopy = (() => {
    if (correlation == null) return 'Correlation: not enough variance to compute';
    const r = correlation;
    if (r > 0.4)  return `Strong positive (r = ${r}). Your high-conviction calls have been outperforming.`;
    if (r > 0.15) return `Mild positive (r = ${r}). Conviction tracks outcomes a bit — keep refining.`;
    if (r > -0.15) return `~0 (r = ${r}). Conviction isn't predicting returns. Common — most investors plateau here.`;
    if (r > -0.4) return `Mild negative (r = ${r}). Your bold calls are slightly underperforming. Worth investigating which entry types drag.`;
    return `Strong negative (r = ${r}). Bold calls are losing. Consider a smaller bet size on high-conviction trades while you study the pattern.`;
  })();

  return (
    <div className="rounded-xl border-2 border-violet-300 dark:border-violet-800 bg-muted/20 p-4 space-y-3" data-testid="conviction-chart">
      <div>
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          <h3 className="text-sm font-bold">Conviction vs Outcome</h3>
          <span className="text-[10px] text-muted-foreground">({count} entries)</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1 inline-flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{corrCopy}</span>
        </p>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="conviction"
              type="number"
              domain={[0.5, 10.5]}
              ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
              label={{ value: 'Conviction (1–10)', position: 'insideBottom', offset: -8, fontSize: 11 }}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              dataKey="return_pct"
              type="number"
              label={{ value: 'Return since entry (%)', angle: -90, position: 'insideLeft', offset: 12, fontSize: 11 }}
              tick={{ fontSize: 11 }}
            />
            <ZAxis range={[80, 80]} />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            {/* y=0 reference so the user can see at-a-glance which
                quadrant each dot lands in. */}
            <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.25} />
            <Scatter data={points} fill="#7c3aed" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
